// Descripción para Instagram desde el lienzo —
// ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// El modal es EL MISMO que el del visor (`DescripcionParaPostModal`,
// ADR-2026-09-16-los-menus-del-visor-son-componentes). El orden del pedido también:
// primero se lee la fila —si la imagen YA tiene descripción se muestra esa y no se
// vuelve a pedir— y recién si está vacía se llama al generador. Sin créditos ni plan.

import { useEffect, useState } from 'react';
import { DescripcionParaPostModal } from '../../../components/DescripcionParaPostModal';
import { esperarPostDescription, postInstagramDescription } from '../../../lib/instagramDescription';
import { supabase } from '../../../lib/supabase';
import type { PropsDeHerramienta } from './herramienta';

export const PanelDescripcion: React.FC<PropsDeHerramienta> = ({ celda, onCerrar, onError }) => {
  const [texto, setTexto] = useState('');
  const [generando, setGenerando] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setGenerando(true);
    setTexto('');
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        const { data: fila } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', celda.imageId)
          .eq('id_company', celda.companyId)
          .maybeSingle();
        const yaEstaba = (fila as { post_description?: string | null } | null)?.post_description;
        if (yaEstaba && yaEstaba.trim()) {
          if (!cancelado) setTexto(yaEstaba);
          return;
        }
        const res = await postInstagramDescription({ user_id: user.id, image_id: celda.imageId });
        if (!res.ok) throw new Error('No pudimos escribir la descripción. Intentá de nuevo.');
        const final =
          res.postDescription ??
          (await esperarPostDescription({
            imageId: celda.imageId,
            companyId: celda.companyId,
            seguirEsperando: () => !cancelado,
          }));
        if (cancelado) return;
        if (final) setTexto(final);
        else throw new Error('La descripción todavía no está lista. Probá de nuevo en unos segundos.');
      } catch (err) {
        if (cancelado) return;
        onError(err instanceof Error ? err.message : 'No pudimos escribir la descripción. Intentá de nuevo.');
        onCerrar();
      } finally {
        if (!cancelado) setGenerando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [celda.imageId, celda.companyId, onCerrar, onError]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      onError('No se pudo copiar la descripción');
    }
  };

  return (
    <DescripcionParaPostModal
      open
      generando={generando}
      texto={texto}
      copiado={copiado}
      onCopiar={() => void copiar()}
      onClose={onCerrar}
    />
  );
};
